import type { ReadingSample } from '../types/domain'
import {
  decodeHubFrame, decodeHubInfo, encodeHubCommand, HUB_FILE_NAMES, hubError,
  type HubFileId, type HubFrame, type HubInfo,
} from './hubProtocol'
import {
  commitHubBlock, getHubFileMeta, resetHubFile, verifyHubFile,
  type HubFileMeta, type HubSessionRef,
} from '../db/offlineDb'
import {
  BLE_UUIDS,
  decodeNotificationFrame,
  decodeRecords,
  encodeControlCommand,
  encodeLegacyCommand,
} from './protocol'
import type { SensorInfo, SensorTransport } from './transport'

const DEFAULT_TRANSFER_TIMEOUT_MS = 30_000
const NAME_PREFIX = import.meta.env.VITE_BLE_NAME_PREFIX?.trim() || 'NOVAROLL'
const HUB_RETRIES = 3

interface DeviceInfoPayload {
  protocolVersion?: number
  serialNumber?: string
  firmwareVersion?: string
  batteryPercent?: number
  recordCount?: number
  sampleIntervalMs?: number
}

export class WebBluetoothTransport implements SensorTransport {
  readonly kind = 'web-bluetooth' as const
  readonly supported = webBluetoothSupported()
  private device?: BluetoothDevice
  private server?: BluetoothRemoteGATTServer
  private service?: BluetoothRemoteGATTService
  private control?: BluetoothRemoteGATTCharacteristic
  private lastTransferId = 0
  private lastSequenceNo = 0
  private abortTransfer?: (error: Error) => void
  private hubData?: BluetoothRemoteGATTCharacteristic
  private hubInfo?: HubInfo

  async requestDevice(): Promise<void> {
    if (!navigator.bluetooth) throw new Error('Web Bluetooth недоступен в этом браузере')
    if (!window.isSecureContext) throw new Error('Для Web Bluetooth откройте приложение по HTTPS или на localhost')
    this.device = await navigator.bluetooth.requestDevice({
      filters: [
        { services: [BLE_UUIDS.service] },
        { namePrefix: NAME_PREFIX },
      ],
      optionalServices: [BLE_UUIDS.service],
    })
  }

  async connect(): Promise<void> {
    if (!this.device?.gatt) throw new Error('BLE-устройство не выбрано')
    this.server = await this.device.gatt.connect()
    this.service = await this.server.getPrimaryService(BLE_UUIDS.service)
    this.device.addEventListener('gattserverdisconnected', this.handleDisconnect)
    this.control = await this.optionalCharacteristic(BLE_UUIDS.control)
  }

  async readInfo(): Promise<SensorInfo> {
    const characteristic = await this.getCharacteristic(BLE_UUIDS.info)
    const value = await characteristic.readValue()
    const text = new TextDecoder('utf-8', { fatal: true }).decode(value)
    const raw = text.trimStart().startsWith('{') ? JSON.parse(text) as { protocolVersion?: number } : undefined
    if (raw?.protocolVersion === 2) {
      if (value.byteLength > 256) throw new Error('INFO хаба превышает допустимые 256 байт')
      const hub = decodeHubInfo(text)
      this.hubInfo = hub
      return {
        deviceId: this.device?.id ?? 'unknown',
        name: this.device?.name ?? `NOVAROLL-HUB-${hub.serialNumber}`,
        serialNumber: hub.serialNumber,
        firmware: hub.firmwareVersion,
        batteryPercent: hub.batteryPercent,
        protocolVersion: 2,
        hub,
      }
    }
    const payload = decodeDeviceInfo(text)
    return {
      deviceId: this.device?.id ?? 'unknown',
      name: this.device?.name ?? payload.serialNumber ?? 'NOVAROLL sensor',
      serialNumber: payload.serialNumber,
      firmware: payload.firmwareVersion,
      batteryPercent: payload.batteryPercent,
      protocolVersion: payload.protocolVersion,
      recordCount: payload.recordCount,
      sampleIntervalMs: payload.sampleIntervalMs,
    }
  }

  async readSamples(onProgress: (completed: number, total?: number) => void): Promise<ReadingSample[]> {
    if (!this.control) return this.readSinglePacket(onProgress)
    return this.readNotificationStream(onProgress)
  }

  async acknowledge(): Promise<void> {
    if (this.hubInfo) {
      const result = await this.sendHubResult('ACK_SESSION', this.hubInfo.sessionToken)
      if (result.sessionStateCode !== 4) throw new Error('Хаб не подтвердил состояние ACKED')
      return
    }
    if (this.control) {
      await this.control.writeValueWithResponse(encodeControlCommand('ACK', this.lastTransferId, this.lastSequenceNo))
      return
    }
    const characteristic = await this.getCharacteristic(BLE_UUIDS.data)
    await characteristic.writeValueWithResponse(encodeLegacyCommand('ACK'))
  }

  async clear(): Promise<void> {
    if (this.hubInfo) {
      const result = await this.sendHubResult('CLEAR_SESSION', this.hubInfo.sessionToken)
      if (result.sessionStateCode !== 0) throw new Error('Хаб не подтвердил очистку сеанса')
      return
    }
    if (this.control) {
      await this.control.writeValueWithResponse(encodeControlCommand('CLEAR', this.lastTransferId, this.lastSequenceNo))
      return
    }
    const characteristic = await this.getCharacteristic(BLE_UUIDS.data)
    await characteristic.writeValueWithResponse(encodeLegacyCommand('CLEAR'))
  }

  disconnect(): void {
    if (this.device) this.device.removeEventListener('gattserverdisconnected', this.handleDisconnect)
    this.server?.disconnect()
    this.resetConnection()
  }

  async readHubFiles(
    hub: HubInfo,
    palletId: string,
    onProgress: (completed: number, total: number, fileName: string) => void,
  ): Promise<{ ref: HubSessionRef; files: HubFileMeta[] }> {
    if (hub.sessionState !== 'READY' && hub.sessionState !== 'ACKED') {
      throw new Error(`Сеанс хаба ещё не готов к выгрузке: ${hub.sessionState}`)
    }
    if (!this.control) throw new Error('У хаба отсутствует CONTROL characteristic')
    const ref: HubSessionRef = {
      palletId, serialNumber: hub.serialNumber, sessionId: hub.sessionId, sessionToken: hub.sessionToken,
    }
    await this.ensureHubNotifications()
    const files: HubFileMeta[] = []
    let completed = 0
    for (let fileId = 1; fileId <= 5; fileId++) {
      const id = fileId as HubFileId
      const fileName = HUB_FILE_NAMES[id - 1]!
      let meta = await getHubFileMeta(ref, id)
      let restartCount = 0
      if (meta?.verified) {
        files.push(meta)
        completed += meta.fileSize
        onProgress(completed, hub.totalBytes, fileName)
        continue
      }
      while (!meta?.verified) {
        let block: HubBlock | undefined
        for (let attempt = 0; attempt < HUB_RETRIES; attempt++) {
          try {
            block = await this.readHubBlock(id, meta?.committedOffset ?? 0)
            break
          } catch (error) {
            if (attempt === HUB_RETRIES - 1) throw error
            await this.reconnectHub(hub)
          }
        }
        if (!block) throw new Error('Не удалось прочитать блок файла хаба')
        meta = await commitHubBlock(ref, id, fileName, block.startOffset, block.fileSize, block.fileCrc32, block.bytes)
        onProgress(completed + meta.committedOffset, hub.totalBytes, fileName)
        if (meta.committedOffset === meta.fileSize) {
          try {
            meta = await verifyHubFile(ref, id)
          } catch (error) {
            if (++restartCount >= 2) throw error
            await resetHubFile(ref, id)
            meta = undefined
          }
        }
      }
      files.push(meta)
      completed += meta.fileSize
    }
    if (completed !== hub.totalBytes) throw new Error(`Объём файлов ${completed} не совпадает с INFO хаба ${hub.totalBytes}`)
    return { ref, files }
  }

  private async ensureHubNotifications(): Promise<BluetoothRemoteGATTCharacteristic> {
    if (this.hubData) return this.hubData
    const characteristic = await this.getCharacteristic(BLE_UUIDS.data)
    this.hubData = await characteristic.startNotifications()
    return this.hubData
  }

  private async reconnectHub(expected: HubInfo): Promise<void> {
    this.server?.disconnect()
    this.resetConnection()
    await this.connect()
    const current = (await this.readInfo()).hub
    if (!current || current.serialNumber !== expected.serialNumber
      || current.sessionId !== expected.sessionId || current.sessionToken !== expected.sessionToken) {
      throw new Error('После переподключения хаб объявил другой сеанс: продолжение запрещено')
    }
    await this.ensureHubNotifications()
  }

  private async readHubBlock(fileId: HubFileId, offset: number): Promise<HubBlock> {
    const transferId = randomTransferId()
    const characteristic = await this.ensureHubNotifications()
    let timer: number | undefined
    let finished = false
    let started: Extract<HubFrame, { type: 'START' }> | undefined
    let expectedOffset = offset
    let invalid: Error | undefined
    const chunks: Uint8Array[] = []
    let resolve!: (value: HubBlock) => void
    let reject!: (error: Error) => void
    const transfer = new Promise<HubBlock>((yes, no) => { resolve = yes; reject = no })
    void transfer.catch(() => undefined)
    const fail = (error: unknown) => {
      if (finished) return
      finished = true
      reject(error instanceof Error ? error : new Error('Ошибка BLE-передачи хаба'))
    }
    const armTimeout = () => {
      if (timer !== undefined) window.clearTimeout(timer)
      timer = window.setTimeout(() => fail(new Error('Хаб не ответил в течение 30 секунд')), DEFAULT_TRANSFER_TIMEOUT_MS)
    }
    const onValue = (event: Event) => {
      const value = (event.target as unknown as BluetoothRemoteGATTCharacteristic).value
      if (!value) return
      let frame: HubFrame
      try {
        frame = decodeHubFrame(value)
      } catch (error) {
        invalid = error instanceof Error ? error : new Error('Некорректный BLE-фрейм')
        return
      }
      armTimeout()
      if (frame.type === 'ERROR') {
        if (frame.transferId === transferId || frame.transferId === 0) fail(hubError(frame))
        return
      }
      if (frame.type === 'START') {
        if (frame.transferId !== transferId) return
        if (started || frame.fileId !== fileId || frame.startOffset !== offset
          || frame.transferBytes !== Math.min(65535, frame.fileSize - offset)
          || offset > frame.fileSize) {
          invalid = new Error('Некорректный START блока хаба')
        } else started = frame
        return
      }
      if (frame.type === 'DATA') {
        if (!started) { invalid = new Error('DATA получен до START'); return }
        if (invalid) return
        if (frame.fileId !== fileId || frame.offset !== expectedOffset || !frame.data.length
          || expectedOffset + frame.data.length > offset + started.transferBytes) {
          invalid = new Error('Нарушен порядок или размер DATA-фреймов хаба')
        } else {
          chunks.push(frame.data)
          expectedOffset += frame.data.length
        }
        return
      }
      if (frame.type === 'END') {
        if (frame.transferId !== transferId) return
        if (!started || frame.fileId !== fileId || frame.status !== 0
          || expectedOffset !== offset + started.transferBytes
          || frame.nextOffset !== expectedOffset || frame.eof !== (expectedOffset === started.fileSize)) {
          invalid ??= new Error('Некорректный END блока хаба')
        }
        if (invalid) { fail(invalid); return }
        if (!started) { fail(new Error('END получен до START')); return }
        const bytes = new Uint8Array(started.transferBytes)
        let position = 0
        for (const chunk of chunks) { bytes.set(chunk, position); position += chunk.length }
        finished = true
        resolve({ startOffset: offset, fileSize: started.fileSize, fileCrc32: frame.fileCrc32, bytes })
      }
    }
    characteristic.addEventListener('characteristicvaluechanged', onValue)
    this.abortTransfer = fail
    try {
      armTimeout()
      await this.control?.writeValueWithResponse(encodeHubCommand('READ_FILE', fileId, transferId, offset))
      return await transfer
    } finally {
      if (timer !== undefined) window.clearTimeout(timer)
      characteristic.removeEventListener('characteristicvaluechanged', onValue)
      this.abortTransfer = undefined
    }
  }

  private async sendHubResult(command: 'ACK_SESSION' | 'CLEAR_SESSION', token: number): Promise<Extract<HubFrame, { type: 'RESULT' }>> {
    if (!this.control) throw new Error('Нет соединения с хабом')
    const characteristic = await this.ensureHubNotifications()
    const transferId = randomTransferId()
    let timer: number | undefined
    let resolve!: (frame: Extract<HubFrame, { type: 'RESULT' }>) => void
    let reject!: (error: Error) => void
    const transfer = new Promise<Extract<HubFrame, { type: 'RESULT' }>>((yes, no) => { resolve = yes; reject = no })
    void transfer.catch(() => undefined)
    const onValue = (event: Event) => {
      try {
        const value = (event.target as unknown as BluetoothRemoteGATTCharacteristic).value
        if (!value) return
        const frame = decodeHubFrame(value)
        if (frame.type === 'ERROR' && (frame.transferId === transferId || frame.transferId === 0)) reject(hubError(frame))
        if (frame.type === 'RESULT' && frame.transferId === transferId) {
          if (frame.command !== (command === 'ACK_SESSION' ? 2 : 3) || frame.sessionToken !== token || frame.status !== 0) {
            reject(new Error('Хаб вернул некорректное подтверждение команды'))
          } else resolve(frame)
        }
      } catch (error) { reject(error instanceof Error ? error : new Error('Некорректный ответ хаба')) }
    }
    characteristic.addEventListener('characteristicvaluechanged', onValue)
    this.abortTransfer = reject
    try {
      timer = window.setTimeout(() => reject(new Error('Хаб не подтвердил команду в течение 30 секунд')), DEFAULT_TRANSFER_TIMEOUT_MS)
      await this.control.writeValueWithResponse(encodeHubCommand(command, 0, transferId, token))
      return await transfer
    } finally {
      if (timer !== undefined) window.clearTimeout(timer)
      characteristic.removeEventListener('characteristicvaluechanged', onValue)
      this.abortTransfer = undefined
    }
  }

  private async readSinglePacket(onProgress: (completed: number, total?: number) => void): Promise<ReadingSample[]> {
    const characteristic = await this.getCharacteristic(BLE_UUIDS.data)
    const value = await characteristic.readValue()
    const samples = decodeRecords(value)
    this.lastSequenceNo = samples.at(-1)?.sequenceNo ?? 0
    onProgress(samples.length, samples.length)
    return samples
  }

  private async readNotificationStream(onProgress: (completed: number, total?: number) => void): Promise<ReadingSample[]> {
    const dataCharacteristic = await this.getCharacteristic(BLE_UUIDS.data)
    const transferId = crypto.getRandomValues(new Uint32Array(1))[0] || 1
    const samples: ReadingSample[] = []
    let announcedTotal: number | undefined
    let transferStarted = false
    let finished = false
    let timer: number | undefined
    let resolveTransfer!: (samples: ReadingSample[]) => void
    let rejectTransfer!: (error: Error) => void

    const transfer = new Promise<ReadingSample[]>((resolve, reject) => {
      resolveTransfer = resolve
      rejectTransfer = reject
    })

    const fail = (error: unknown) => {
      if (finished) return
      finished = true
      rejectTransfer(error instanceof Error ? error : new Error('Ошибка BLE-передачи'))
    }
    const armTimeout = () => {
      if (timer !== undefined) window.clearTimeout(timer)
      timer = window.setTimeout(() => fail(new Error('Датчик не завершил передачу за отведённое время')), transferTimeoutMs())
    }
    const onValue = (event: Event) => {
      try {
        const characteristic = event.target as unknown as BluetoothRemoteGATTCharacteristic
        if (!characteristic.value) throw new Error('Получено пустое BLE-уведомление')
        const frame = decodeNotificationFrame(characteristic.value)
        armTimeout()
        if (frame.type === 'START') {
          if (frame.transferId !== transferId) throw new Error('Получен ответ от другой BLE-передачи')
          if (frame.totalRecords === 0 || frame.totalRecords > 10_000) {
            throw new Error(`Некорректное количество записей в передаче: ${frame.totalRecords}`)
          }
          transferStarted = true
          announcedTotal = frame.totalRecords
          onProgress(0, announcedTotal)
          return
        }
        if (frame.type === 'RECORD') {
          if (!transferStarted) throw new Error('Запись получена до START-фрейма')
          samples.push(frame.sample)
          onProgress(samples.length, announcedTotal)
          return
        }
        if (frame.type === 'ERROR') throw new Error(`Датчик вернул ошибку ${frame.code}: ${frame.message || 'без описания'}`)
        if (!transferStarted || frame.transferId !== transferId) throw new Error('Некорректный END-фрейм передачи')
        if (frame.status !== 0) throw new Error(`Датчик завершил передачу со статусом ${frame.status}`)
        if (frame.sentRecords !== samples.length || announcedTotal !== samples.length) {
          throw new Error(`Передача неполная: получено ${samples.length}, заявлено ${announcedTotal}, отправлено ${frame.sentRecords}`)
        }
        const unique = new Set(samples.map(sample => `${sample.sequenceNo}:${sample.channel}`))
        if (unique.size !== samples.length) throw new Error('В BLE-передаче обнаружены дубли sequence/channel')
        this.lastTransferId = transferId
        this.lastSequenceNo = frame.lastSequenceNo
        finished = true
        resolveTransfer(samples)
      } catch (error) {
        fail(error)
      }
    }

    dataCharacteristic.addEventListener('characteristicvaluechanged', onValue)
    this.abortTransfer = fail
    try {
      await dataCharacteristic.startNotifications()
      armTimeout()
      await this.control?.writeValueWithResponse(encodeControlCommand('READ', transferId))
      return await transfer
    } finally {
      finished = true
      if (timer !== undefined) window.clearTimeout(timer)
      dataCharacteristic.removeEventListener('characteristicvaluechanged', onValue)
      await dataCharacteristic.stopNotifications().catch(() => undefined)
      this.abortTransfer = undefined
    }
  }

  private async getCharacteristic(uuid: string): Promise<BluetoothRemoteGATTCharacteristic> {
    if (!this.service) throw new Error('Нет активного BLE-соединения')
    return this.service.getCharacteristic(uuid)
  }

  private async optionalCharacteristic(uuid: string): Promise<BluetoothRemoteGATTCharacteristic | undefined> {
    try {
      return await this.getCharacteristic(uuid)
    } catch {
      return undefined
    }
  }

  private readonly handleDisconnect = () => {
    this.abortTransfer?.(new Error('Соединение с BLE-датчиком разорвано'))
    this.resetConnection()
  }

  private resetConnection() {
    this.server = undefined
    this.service = undefined
    this.control = undefined
    this.hubData = undefined
    this.hubInfo = undefined
  }
}

interface HubBlock {
  startOffset: number
  fileSize: number
  fileCrc32: number
  bytes: Uint8Array
}

function randomTransferId(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] || 1
}

export function webBluetoothSupported(): boolean {
  return typeof navigator.bluetooth !== 'undefined' && window.isSecureContext
}

function decodeDeviceInfo(text: string): DeviceInfoPayload {
  const normalized = text.replace(/\0+$/, '').trim()
  if (normalized.startsWith('{')) {
    let value: unknown
    try {
      value = JSON.parse(normalized)
    } catch {
      throw new Error('Характеристика INFO содержит некорректный JSON')
    }
    if (!value || typeof value !== 'object') throw new Error('Характеристика INFO имеет неверный формат')
    const payload = value as DeviceInfoPayload
    if (payload.protocolVersion !== 1) {
      throw new Error(`Версия протокола датчика ${String(payload.protocolVersion)} не поддерживается`)
    }
    if (!payload.serialNumber) throw new Error('Датчик не передал serialNumber')
    if (payload.batteryPercent !== undefined && (payload.batteryPercent < 0 || payload.batteryPercent > 100)) {
      throw new Error('Датчик передал некорректный заряд батареи')
    }
    return payload
  }

  const [firmwareVersion, battery] = normalized.split('|')
  return {
    firmwareVersion: firmwareVersion || undefined,
    batteryPercent: battery ? Number.parseInt(battery, 10) : undefined,
  }
}

function transferTimeoutMs(): number {
  const configured = Number(import.meta.env.VITE_BLE_TRANSFER_TIMEOUT_MS)
  return Number.isFinite(configured) && configured >= 5_000 ? configured : DEFAULT_TRANSFER_TIMEOUT_MS
}
